import { useState } from 'react'
import { AlertTriangle, Flag, Info, TrendingUp } from 'lucide-react'
import { BoxTruckPricingConfidence, BoxTruckPricingQuote, BoxTruckPricingRequest } from '@numeo/types'
import { CanonicalLoad, formatUsdWhole } from '@numeo/types-core'
import { Button } from '@/components/ds2'
import type { BoxTruckPricingState } from '../hooks/useBoxTruckPricing'
import { BoxTruckPricingReportDialog } from './BoxTruckPricingReportDialog'

/**
 * What to ask the broker and what to offer the driver, for the load the drawer has open.
 *
 * Two of the four numbers are measured and two are chosen, and the panel is laid out so a dispatcher can
 * tell which is which without being told twice. The pair is the headline, and under it, at the same
 * weight, the history it rests on: how many loads this lane and this broker have actually paid, then the
 * typical rate and the range it came from. The suggestion is only worth what is underneath it.
 *
 * The acceptance probabilities the pair is picked with are deliberately NOT shown. They are assumptions —
 * no broker reply or driver offer is logged yet — and a percentage on screen is read as a measurement
 * whatever the caption says. When they become measured, they earn a place here.
 */

const TONE: Record<BoxTruckPricingConfidence, string> = {
    high: 'bg-success-soft text-success-soft-foreground',
    medium: 'bg-warning-soft text-warning-soft-foreground',
    low: 'bg-default text-default-foreground',
}

/**
 * The badge names WHY, not just how much.
 *
 * `medium` covers three situations and they do not deserve the same word: a thin lane; a well-measured
 * lane whose broker the tenant has never booked — about half this board, at the 52% broker match rate;
 * and a well-measured lane on a post that named no broker at all, where there was nothing to look up.
 * "THIN LANE" beside "40 lane loads", or "NEW BROKER" beside "no broker on the post", is the panel
 * contradicting itself one line later.
 */
const confidenceLabel = (confidence: BoxTruckPricingConfidence, laneLoads: number, brokerHistoryLoads: number, brokerNamed: boolean): string => {
    if (confidence === 'high') return 'Lane measured'
    if (confidence === 'low') return 'Estimated'
    // `medium` with a well-measured lane is always the BROKER side; `high` needs both, so reaching here
    // with twelve lane loads means the broker factor is the half that is missing.
    if (laneLoads < 12) return 'Thin lane'
    if (!brokerNamed) return 'No broker named'
    // Decided on the HISTORY count, which is the one on screen. Judging it on the factor's sample put
    // "NEW BROKER" directly above "With this broker  33" — the panel contradicting its own evidence row
    // one line later, which is the thing the row was split out to stop.
    return brokerHistoryLoads > 0 ? 'Broker not scored' : 'New broker'
}

/** A margin this wide is outside what this desk usually books, and the model has never been checked there. */
const WIDE_MARGIN_SHARE = 0.25

/**
 * What a figure works out to per mile.
 *
 * Deliberately NOT `ratePerMileUsd` from `@numeo/types`, which owns the $/mile of a POSTED load: its
 * whole job is the `MIN_POSTED_RATE_USD` rule, which reads a board's "$1" placeholder as unpriced. These
 * two numbers are the model's own, and a driver offer legitimately starts at $40 on a short run — the
 * owner would answer `null` for a real offer. Same arithmetic, different question.
 */
const perMile = (amount: number, miles: number): string | undefined => (miles > 0 ? `$${(amount / miles).toFixed(2)}/mi` : undefined)

/** `hint` sits just before the figure, smaller and muted — a unit to read past, not a second number. */
const Row = ({ label, value, hint, strong }: { label: string; value: string; hint?: string; strong?: boolean }) => (
    <div className="flex items-baseline justify-between gap-3">
        <span className={`text-xs ${strong ? 'font-semibold text-foreground' : 'text-foreground-muted'}`}>{label}</span>
        <span className="flex items-baseline gap-1.5">
            {hint && <span className="font-mono text-[10px] tabular-nums text-foreground-muted">{hint}</span>}
            <span className={`font-mono tabular-nums ${strong ? 'text-lg font-bold text-foreground' : 'text-sm font-semibold text-foreground'}`}>{value}</span>
        </span>
    </div>
)

/** The panel owns its own placement, so that when it renders nothing it also occupies nothing — a fixed
 *  column around it would leave a hole in the workspace for everyone without the Gluon tick. */
const Shell = ({ className, children }: { className?: string; children: React.ReactNode }) => (
    <section className={`rounded-lg border border-border bg-background p-3 ${className ?? ''}`}>
        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
            <TrendingUp size={12} /> Suggested pricing
        </div>
        {children}
    </section>
)

const Quiet = ({ className, children }: { className?: string; children: React.ReactNode }) => (
    <Shell className={className}>
        <p className="text-xs text-foreground-muted">{children}</p>
    </Shell>
)

const Quote = ({ quote, onReport, className }: { quote: BoxTruckPricingQuote; onReport: () => void; className?: string }) => {
    const { evidence } = quote
    // Admin and main-service deploy separately, so a quote can arrive from a main-service that
    // predates `brokerHistoryLoads`. Reading it straight off then threw on `undefined.toLocaleString()`
    // for any named broker, and told the badge every broker was new. The factor's own count is what
    // this row showed before, which is the right thing to fall back to.
    const brokerHistoryLoads = evidence.brokerHistoryLoads ?? evidence.brokerLoads
    const label = confidenceLabel(quote.confidence, evidence.laneLoads, brokerHistoryLoads, evidence.brokerNamed)
    const hasPair = quote.suggestedQuote > 0
    // The margin the two printed numbers produce, not the server's net-of-factoring figure.
    //
    // A dispatcher checks this row by subtracting the two above it, and the factoring fee made that
    // subtraction fail by a few dollars every time — $701 − $499 printed as $195, not $202. The fee is
    // the back office's concern and the desk does not quote against it. The server still scores and
    // RECORDS the net figure (`expectedMargin`), which is what the $75 minimum is enforced on, so the
    // number shown here is never below what that floor promised.
    const margin = quote.suggestedQuote - quote.suggestedDriverPay
    const wideMargin = hasPair && margin > quote.suggestedQuote * WIDE_MARGIN_SHARE
    return (
        <Shell className={className}>
            {hasPair ? (
                <div className="space-y-1.5">
                    {/* Both per LOADED mile, which is the only distance this model knows: the lane's own,
                        and the one the tenant pays its drivers on. A dispatcher reads the board in $/mi
                        and the quote in dollars, so the pair is only comparable with both on screen. */}
                    <Row label="Quote broker" value={formatUsdWhole(quote.suggestedQuote)} hint={perMile(quote.suggestedQuote, quote.loadedMiles)} strong />
                    <Row
                        label="Offer driver"
                        value={formatUsdWhole(quote.suggestedDriverPay)}
                        hint={perMile(quote.suggestedDriverPay, quote.loadedMiles)}
                        strong
                    />
                    <div className="flex items-baseline justify-between gap-3 border-t border-border pt-1.5">
                        <span className="text-xs font-semibold text-foreground">Margin</span>
                        <span className="font-mono text-lg font-bold tabular-nums text-success">{formatUsdWhole(margin)}</span>
                    </div>
                </div>
            ) : (
                /* No reason is given here on purpose. There are three — the margin is too thin, the post sits
                   under the lane, the post is implausible — and the server names the right one in `notes`
                   below. Hard-coding the margin one had the panel blaming the minimum margin for a post it
                   had refused to price at all, with the true reason printed underneath it. */
                <p className="text-xs text-foreground-muted">
                    No pair to suggest on this one — this lane typically pays {formatUsdWhole(quote.referenceRate)} and the driver side starts around{' '}
                    {formatUsdWhole(quote.typicalDriverPay)}.
                </p>
            )}

            {/* How much history the pair above is standing on, at the same weight as the pair itself.
                These two counts decide what the suggestion is worth — a quote off nine lane loads and a
                quote off ninety are different claims — and as a tail on the badge line below they were
                read as a caption. The broker count prints a dash rather than a zero when the POST named
                no broker: "0 with this broker" would say the tenant has never booked a company nobody
                named.

                `brokerHistoryLoads`, not `brokerLoads`: this row is checked against the TMS, and the
                model's own sample is a third of it — one equipment, inside the build window, inside the
                $/mi band the fit needs. Fourteen brokers this desk had really hauled for read "0" here,
                which is the opposite of what the row is for. The narrow count still drives the maths. */}
            <div className="mt-2.5 space-y-1.5 border-t border-border pt-2">
                <Row label="Lane loads" value={evidence.laneLoads.toLocaleString('en-US')} strong />
                <Row label="With this broker" value={evidence.brokerNamed ? brokerHistoryLoads.toLocaleString('en-US') : '—'} strong />
            </div>

            {/* The measured half, under the chosen half: what this lane has actually paid. */}
            <div className="mt-2.5 space-y-1 border-t border-border pt-2">
                <Row label="Typical for this lane" value={formatUsdWhole(quote.referenceRate)} />
                <Row label="Half of loads land in" value={`${formatUsdWhole(quote.typicalLow)} – ${formatUsdWhole(quote.typicalHigh)}`} />
                <Row label="Typical driver pay" value={formatUsdWhole(quote.typicalDriverPay)} />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${TONE[quote.confidence]}`}>{label}</span>
                <span className="text-[11px] text-foreground-muted">
                    diesel ${evidence.diesel.toFixed(2)} vs ${evidence.historyDiesel.toFixed(2)} in history
                </span>
            </div>

            {wideMargin && (
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-warning-soft-foreground">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    This margin is well above what this desk usually books on a load this size — the model has not been checked against outcomes up here.
                </p>
            )}

            {quote.notes.length > 0 && (
                <ul className="mt-2 space-y-1">
                    {quote.notes.map((note) => (
                        <li key={note} className="flex items-start gap-1.5 text-[11px] text-foreground-muted">
                            <Info size={12} className="mt-0.5 shrink-0" />
                            {note}
                        </li>
                    ))}
                </ul>
            )}

            <p className="mt-2 text-[10px] text-foreground-muted">
                Learned from {evidence.modelTrips.toLocaleString('en-US')} delivered loads, built {new Date(evidence.modelBuiltAt).toLocaleDateString()}.
                Suggestions, not a forecast.
            </p>

            {/* A real button, full width, not the 10px text link this started as — a dispatcher reading a
                number they disagree with has to SEE that there is something to press, and a muted caption
                under a paragraph of caveats is not it. Secondary rather than accent: it is the panel's
                escape hatch, not its purpose.

                The label is short because the button is narrow (the drawer's column is 290px) and because
                the card it sits in already says what the problem would be about. The dialog says the rest.

                The button only — its dialog is mounted by the panel, outside the status switch, so a
                re-price cannot unmount a half-written complaint. See `BoxTruckPricingReportDialog`. */}
            <Button variant="secondary" size="sm" onClick={onReport} prefix={<Flag className="h-4 w-4" />} className="mt-3 w-full">
                Report a problem
            </Button>
        </Shell>
    )
}

/**
 * Renders nothing at all without the Gluon pricing tick — the panel is the pilot's whole surface area.
 *
 * Takes the state rather than fetching it: the bid email needs the same quote, to record what the
 * dispatcher did with it, and two components asking for one load would be two requests for one answer.
 *
 * It owns the report dialog rather than the `ready` branch below, and that is not tidiness. The board is
 * live, so `state` flips back to `loading` whenever the load's rate, broker or coordinates change — and a
 * dialog mounted under `ready` went with it, deleting whatever the dispatcher had typed. Here it survives
 * the switch, holding a snapshot of the quote it was opened on.
 */
export const BoxTruckPricingPanel = ({ state, load, className }: { state: BoxTruckPricingState; load: CanonicalLoad; className?: string }) => {
    const [reporting, setReporting] = useState<{ quote: BoxTruckPricingQuote; request: BoxTruckPricingRequest } | null>(null)

    const body = (() => {
        switch (state.status) {
            case 'hidden':
                return null
            case 'loading':
                return <Quiet className={className}>Pricing this lane…</Quiet>
            case 'unpriceable':
                return <Quiet className={className}>{state.reason}</Quiet>
            case 'no-model':
                return <Quiet className={className}>No pricing model has been built for this company yet.</Quiet>
            case 'error':
                return <Quiet className={className}>Could not price this load. Reopen it to try again.</Quiet>
            case 'ready':
                return <Quote quote={state.quote} className={className} onReport={() => setReporting({ quote: state.quote, request: state.request })} />
        }
    })()

    // Without the Gluon tick the panel renders nothing AND occupies nothing — a fixed column would leave
    // a hole in the workspace for everyone who does not have it.
    if (!body && !reporting) return null

    return (
        <>
            {body}
            {reporting && <BoxTruckPricingReportDialog load={load} request={reporting.request} quote={reporting.quote} onClose={() => setReporting(null)} />}
        </>
    )
}
